import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, Plus, PencilSimple, Trash, MagnifyingGlass, ArrowUp, ArrowDown, ArrowsDownUp, Export, UploadSimple, DownloadSimple, X } from "@phosphor-icons/react";
import { useTableFeatures, exportCSV } from "@/hooks/useTableFeatures";

const fmt = (n) => (!n || n === 0) ? "$0" : `$${Number(n).toLocaleString()}`;

const overdueDays = (item) => {
  if ((item.check_number || "").trim()) return null; // check filled → paid
  if (!item.due_date) return null;
  const due = new Date(item.due_date);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - due) / 86400000);
  return diff > 0 ? diff : null;
};

const defaultForm = {
  client_id: "", entity_name: "", invoice_number: "",
  issue_date: "", due_date: "", check_number: "",
  received_date: "", deposit_date: "", amount: 0, notes: "",
  status: "auto",
};

export default function ClientInvoices() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });

  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/client-invoices/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = "client_invoices_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download template"); }
  };

  const openImport = () => { setImportOpen(true); setImportResult(null); setSelectedFile(null); setImporting(false); };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.match(/\.(csv|xlsx?|xls)$/i)) { toast.error("Use .csv or .xlsx"); return; }
    setSelectedFile(f);
  };

  const runImport = async () => {
    if (!selectedFile) return;
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append("file", selectedFile);
    try {
      const { data } = await api.post("/client-invoices/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported ${data.imported} invoices`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => { load(); loadClients(); }, []);
  const load = async () => { try { const { data } = await api.get("/client-invoices"); const sorted = (data || []).slice().sort((a, b) => (a.entity_name || "").localeCompare(b.entity_name || "")); sorted.forEach((it) => { it._overdue_days = overdueDays(it) ?? ""; }); setItems(sorted); } catch { toast.error("Failed to load invoices"); } };
  const loadClients = async () => { try { const { data } = await api.get("/clients"); setClients(data); } catch {} };

  const { search, setSearch, sortKey, sortDir, toggleSort, processed: searched } = useTableFeatures(items);

  // Funding Year filter (fixed FY2021-FY2025 list)
  const [yearFilter, setYearFilter] = useState("all");
  const FY_OPTIONS = ["2027", "2026", "2025", "2024", "2023", "2022", "2021"];
  const processed = yearFilter === "all" ? searched : searched.filter((i) => (i.issue_date || "").startsWith(yearFilter));

  const csvCols = [
    { label: "Entity Name", key: "entity_name" },
    { label: "Invoice Number", key: "invoice_number" },
    { label: "Issue Date", key: "issue_date" },
    { label: "Due Date", key: "due_date" },
    { label: "Check #", key: "check_number" },
    { label: "Received Date", key: "received_date" },
    { label: "Deposit Date", key: "deposit_date" },
    { label: "Amount", key: "amount" },
    { label: "Overdue (days)", key: "_overdue_days" },
  ];

  const SH = ({ k, children }) => (
    <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold cursor-pointer select-none hover:text-[#1A1D20]" onClick={() => toggleSort(k)}>
      <div className="flex items-center gap-1">{children}{sortKey === k ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowsDownUp size={10} className="opacity-30" />}</div>
    </TableHead>
  );

  const save = async () => {
    if (!form.entity_name.trim()) { toast.error("Entity name is required"); return; }
    try {
      if (editing) { await api.put(`/client-invoices/${editing}`, form); toast.success("Updated"); }
      else { await api.post("/client-invoices", form); toast.success("Created"); }
      setOpen(false); load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try { await api.delete(`/client-invoices/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  const edit = (item) => { setForm({ ...defaultForm, ...item }); setEditing(item.id); setOpen(true); };
  const openNew = () => { setForm({ ...defaultForm }); setEditing(null); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectClient = (id) => {
    const c = clients.find((cl) => cl.id === id);
    setForm((f) => ({ ...f, client_id: id, entity_name: c ? c.name : "" }));
  };

  const inlineUpdate = async (item, key, value) => {
    setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, [key]: value } : x));
    try {
      await api.put(`/client-invoices/${item.id}`, { ...item, [key]: value });
    } catch {
      toast.error("Failed to save");
      load();
    }
  };

  return (
    <div className="p-6" data-testid="client-invoices-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Management</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Client Invoices</h1>
          <p className="text-sm text-[#6B7280] mt-1">Track invoices billed to clients, payments received, and deposits</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="ci-template-btn" onClick={downloadTemplate} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-sm flex items-center gap-2 transition-colors">
            <DownloadSimple size={16} weight="bold" /> Template
          </button>
          <button data-testid="ci-import-btn" onClick={openImport} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-sm flex items-center gap-2 transition-colors">
            <UploadSimple size={16} weight="bold" /> Import
          </button>
          <button data-testid="add-client-invoice-btn" onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
            <Plus size={16} weight="bold" /> Add Invoice
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input data-testid="search-client-invoices" placeholder="Search invoices..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger data-testid="filter-ci-year" className="rounded-none w-[140px]"><SelectValue placeholder="All Years" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Years</SelectItem>
            {FY_OPTIONS.map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#6B7280]">{processed.length} invoice{processed.length !== 1 ? "s" : ""}</span>
        <button data-testid="export-ci-btn" onClick={() => exportCSV(processed, csvCols, "client_invoices.csv")} className="ml-auto flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs transition-colors">
          <Export size={14} /> Export CSV
        </button>
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="data-table-header">
              <SH k="entity_name">Entity Name</SH>
              <SH k="invoice_number">Invoice #</SH>
              <SH k="issue_date">Issue Date</SH>
              <SH k="due_date">Due Date</SH>
              <SH k="check_number">Check #</SH>
              <SH k="received_date">Received</SH>
              <SH k="deposit_date">Deposit</SH>
              <SH k="amount">Amount</SH>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Overdue</TableHead>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {processed.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-[#6B7280] text-sm">
                <Receipt size={28} className="mx-auto text-[#6B7280] mb-2 opacity-60" />
                No client invoices yet. Click "Add Invoice" to get started.
              </TableCell></TableRow>
            ) : processed.map((item) => {
              const od = overdueDays(item);
              return (
              <TableRow key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                <TableCell className="py-3 px-4 text-[#1A1D20] font-medium">{item.entity_name || "—"}</TableCell>
                <TableCell className="py-3 px-4 font-mono text-xs text-[#1A1D20]">{item.invoice_number || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.issue_date || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.due_date || "—"}</TableCell>
                <TableCell className="py-3 px-4">
                  <Input
                    data-testid={`ci-check-${item.id}`}
                    value={item.check_number || ""}
                    onChange={(e) => setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, check_number: e.target.value } : x))}
                    onBlur={(e) => e.target.value !== (item.check_number || "") && inlineUpdate(item, "check_number", e.target.value)}
                    className="rounded-none h-8 text-xs px-2 w-[120px] font-mono"
                    placeholder="—"
                  />
                </TableCell>
                <TableCell className="py-3 px-4">
                  <Input
                    data-testid={`ci-received-${item.id}`}
                    type="date"
                    value={item.received_date || ""}
                    onChange={(e) => inlineUpdate(item, "received_date", e.target.value)}
                    className="rounded-none h-8 text-xs px-2 w-[140px]"
                  />
                </TableCell>
                <TableCell className="py-3 px-4">
                  <Input
                    data-testid={`ci-deposit-${item.id}`}
                    type="date"
                    value={item.deposit_date || ""}
                    onChange={(e) => inlineUpdate(item, "deposit_date", e.target.value)}
                    className="rounded-none h-8 text-xs px-2 w-[140px]"
                  />
                </TableCell>
                <TableCell className="py-3 px-4 text-sm font-medium text-[#002FA7]">{item.amount > 0 ? fmt(item.amount) : "—"}</TableCell>
                <TableCell className="py-3 px-4 text-xs" data-testid={`ci-overdue-${item.id}`}>
                  {item.status === "paid" || (item.status !== "overdue" && (item.check_number || "").trim()) ? (
                    <span className="inline-block px-2 py-0.5 border border-green-600 text-green-700 bg-green-50 rounded-none">Paid</span>
                  ) : item.status === "pending" ? (
                    <span className="inline-block px-2 py-0.5 border border-yellow-600 text-yellow-800 bg-yellow-50 rounded-none">Pending</span>
                  ) : item.status === "draft" ? (
                    <span className="inline-block px-2 py-0.5 border border-gray-400 text-[#6B7280] bg-gray-50 rounded-none">Draft</span>
                  ) : od !== null || item.status === "overdue" ? (
                    <span className="inline-block px-2 py-0.5 border border-[#FF2A00] text-white bg-[#FF2A00] rounded-none font-semibold uppercase tracking-wide">Overdue{od !== null ? ` · ${od}d` : ""}</span>
                  ) : (
                    <span className="text-[#6B7280]">—</span>
                  )}
                </TableCell>
                <TableCell className="py-3 px-4">
                  <div className="flex items-center gap-1">
                    <button data-testid={`edit-ci-${item.id}`} onClick={() => edit(item)} className="p-1.5 hover:bg-gray-100"><PencilSimple size={14} className="text-[#6B7280]" /></button>
                    <button data-testid={`delete-ci-${item.id}`} onClick={() => remove(item.id)} className="p-1.5 hover:bg-red-50"><Trash size={14} className="text-[#FF2A00]" /></button>
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editing ? "Edit" : "Add"} Client Invoice</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Entity *</label>
                <Select value={form.client_id} onValueChange={selectClient}>
                  <SelectTrigger data-testid="ci-client-select" className="rounded-none"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent className="rounded-none max-h-[200px]">
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!form.client_id && <Input data-testid="ci-entity-input" value={form.entity_name} onChange={(e) => set("entity_name", e.target.value)} className="rounded-none mt-2" placeholder="Or type entity name" />}
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Invoice Number</label>
                <Input data-testid="ci-invoice-num" value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} className="rounded-none" placeholder="e.g. INV-2026-001" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Issue Date</label>
                <Input data-testid="ci-issue-date" type="date" value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Due Date</label>
                <Input data-testid="ci-due-date" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Check Number</label>
                <Input data-testid="ci-check-num" value={form.check_number} onChange={(e) => set("check_number", e.target.value)} className="rounded-none font-mono" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Received Date</label>
                <Input data-testid="ci-received-date" type="date" value={form.received_date} onChange={(e) => set("received_date", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Deposit Date</label>
                <Input data-testid="ci-deposit-date" type="date" value={form.deposit_date} onChange={(e) => set("deposit_date", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Amount ($)</label>
                <Input data-testid="ci-amount" type="number" value={form.amount} onChange={(e) => set("amount", parseFloat(e.target.value) || 0)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Status</label>
                <Select value={form.status || "auto"} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="ci-status-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="auto">Auto (from dates)</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Notes</label>
              <textarea data-testid="ci-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="cancel-ci-btn" onClick={() => setOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm">Cancel</button>
            <button data-testid="save-ci-btn" onClick={save} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded-none sm:max-w-[560px]">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Import Client Invoices</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            {!importResult && (
              <>
                <div className="text-sm text-[#6B7280]">
                  Upload a CSV or Excel file with these columns: <code className="bg-gray-100 px-1 text-xs">entity_name, invoice_number, issue_date, due_date, check_number, received_date, deposit_date, amount, notes</code>
                </div>
                <button onClick={downloadTemplate} className="text-xs text-[#002FA7] hover:underline flex items-center gap-1">
                  <DownloadSimple size={12} /> Download blank template
                </button>
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} className="hidden" />
                <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-gray-300 hover:border-[#002FA7] hover:bg-[#F8F9FA] rounded-none p-8 text-center cursor-pointer transition-colors">
                  <UploadSimple size={28} className="mx-auto text-[#6B7280] mb-2" />
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2 text-sm">
                      <span className="font-medium text-[#1A1D20]">{selectedFile.name}</span>
                      <button onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }} className="text-[#6B7280] hover:text-[#FF2A00]"><X size={14} /></button>
                    </div>
                  ) : (
                    <p className="text-sm text-[#6B7280]">Click to choose a CSV or Excel file</p>
                  )}
                </div>
              </>
            )}
            {importResult && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#6B7280]">Imported</span><span className="font-medium text-green-700">{importResult.imported}</span></div>
                <div className="flex justify-between"><span className="text-[#6B7280]">Skipped</span><span className="font-medium text-[#FF2A00]">{importResult.skipped}</span></div>
                <div className="flex justify-between"><span className="text-[#6B7280]">Total rows</span><span className="font-medium">{importResult.total}</span></div>
                {importResult.errors?.length > 0 && (
                  <div className="border border-orange-200 bg-orange-50 p-2 mt-3 max-h-[150px] overflow-y-auto">
                    {importResult.errors.map((er, i) => <div key={i} className="text-xs text-[#FF2A00]">{er}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setImportOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm">Close</button>
            {!importResult && (
              <button data-testid="run-ci-import-btn" onClick={runImport} disabled={!selectedFile || importing} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm disabled:opacity-50">
                {importing ? "Importing..." : "Import"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
